import { IRealTimeTranscriptionConnection } from '../interfaces/IRealTimeTranscriptionConnection'
import { ITranscription, ITranscriptionSegment } from "../interfaces/ITranscriptionManager";
import { VoiceSession, SpeechToTextClient, DictationSettingsManager, MicrophoneError } from '@1js/al-voice-client';
import { SpeechToTextProfile } from '@1js/al-voice-client/lib/VoiceSession';
import { SpeechToTextResultType } from '@1js/al-voice-client/lib/client/SpeechToTextClient';
import { secFrom100nsec, msFrom100nsec, GetNumericSpeakerIdFromSpeakerId, secFromMs } from '../utils/Utils'
import { LogEvents } from '../../utils/LogEvents';
import { AugloopRealTimeTranscriptionConnectionKeys } from './telemetry/AugloopRealTimeTranscriptionConnectionKeys';
import { ISpeechSessionStarted } from '@augloop/types-voice';
import { RealTimeTranscriptionConnectionError, RealTimeTranscriptionConnectionErrors } from '../transcriptionManager/RealTimeTranscriptionConnectionErrors';
import { EnsureAugloopRuntimeManager, GetAugloopEventEmitter } from './Augloop';
import { AugloopEvent } from '../interfaces/IAugloopEventEmitter';
import { IAugloopSpeechObserverDecorator, IError } from '@1js/al-voice-client/lib/interface/IAugloopSpeechObserverDecorator';
import { GetDefaultTranscriptionLocale, GetLanguageFromLocale } from '../../utils/TranscriptionLanguages';
import { EventEmitter } from 'events';
import { ERROR_AUGLOOP_ACTIVE_ANNOTATIONS, MAX_UNRESPONSIVENESS_WAIT_TIME_MILLISECONDS, RETRY_SETTINGS } from '../../AppConstants';
import { LogError } from '../../utils/ErrorTelemetry';
import { LogEventNames } from '../../backend/interfaces/ITelemetryLogger';
import { ErrorStringFromException, wait } from '../../utils/utils';

const FD_USE_AUTOPUNCTUATION_INTELLIGENT = 'Intelligent'; // Enable auto-punctuation.

DictationSettingsManager.dictationSettings = {
  dictationLanguage: GetDefaultTranscriptionLocale(),
  useAutoPunctuation: FD_USE_AUTOPUNCTUATION_INTELLIGENT,
  useCorrections: 'true'
};

interface ISpeechEventEmitter {
  on(event: 'SpeechSessionStopped', callback: () => void): void,
  once(event: 'SpeechSessionStopped', callback: () => void): void,
  emit(event: 'SpeechSessionStopped'): void
}

type CTSSpeakerIDWithOffsetKey = string;

// Returns a string value in the form "<ctsSpeakerId, offset>" to be used as key on transcriptSegmentsMap.
// Note that this string is not aware of the format of ctsSpeakerId and offset, so it should only be used as key
// for the map, we should not attempt to extract values from it.
function GetCTSSpeakerIDWithOffsetKey(ctsSpeakerId: string, offset: number): CTSSpeakerIDWithOffsetKey {
  return `<${ctsSpeakerId}, ${offset}>`
}

export class AugloopRealTimeTranscriptionConnection implements IRealTimeTranscriptionConnection {
    private speechToTextClient: SpeechToTextClient | null = null;
    private voiceSession: VoiceSession | null = null;
    private transcription: ITranscription;
    private isFinalTranscriptAvailable: boolean = false;
    private onAudioBuffer: (buffer: AudioBuffer) => void;
    private isVoiceSessionActive: boolean = false;
    private offsetAdjustmentMs: number = 0;
    private speechSessionStartedTime: number | null = null;
    private lastPausedTime: number | null = null;
    private pauseAdjustmentMs: number = 0;
    private onError: (error: Error) => void;
    private maxSpeakerId: number | null = null;
    private initPromise: Promise<void>;
    private maxProgressUpdateTimestamp: number = 0;
    private sessionEndOffsets: number[] = []; // Contains a list of the time offsets where each CTS session ends.
    private closeConnectionPromise: Promise<void> | null = null;
    private locale: string | null = null;
    private speechEventEmitter: ISpeechEventEmitter;
    private attempt: number = 0;

    // Keep a map of transcript segments indexed by CTS Speaker ID and adjusted offset (offset is a way to uniquely identify each result).
    // The map contains segments in the order we initially received them. When receiving NearRT results at the end of the session,
    // some of the entries may be overwritten by results with corrected speakerIDs.
    private transcriptSegmentsMap: Map<CTSSpeakerIDWithOffsetKey, ITranscriptionSegment>;

    private fdSpeechClientObserver: IAugloopSpeechObserverDecorator = {
      onSpeechSessionStarted: async (speechSessionStarted: ISpeechSessionStarted) => {
        try {
          LogEvents.AugloopRealTimeTranscriptionConnection.IncrementCount(AugloopRealTimeTranscriptionConnectionKeys.OnSpeechSessionStarted);

          if (speechSessionStarted != null) {
            LogEvents.AugloopRealTimeTranscriptionConnection.AddData([{key: AugloopRealTimeTranscriptionConnectionKeys.SpeechSessionId, value: speechSessionStarted.speechSessionId}]);
            console.log("Transcription speech session ID: ", speechSessionStarted.speechSessionId);
          }

          if (this.speechSessionStartedTime != null) {
            // It is possible for a cognitive services session to be restarted (and hence get reset speech event offsets).
            // If this happens, attempt to adjust the received offsets by the time difference between the initial onSpeechSessionStarted event and the latest one.
            // When a session is reset, we need to subtract the time the user was paused for from our offset adjustment, since we don't want that time to be included
            // in the transcript timestamps.
            this.offsetAdjustmentMs = Date.now() - this.speechSessionStartedTime - this.pauseAdjustmentMs;
            LogEvents.AugloopRealTimeTranscriptionConnection.AddData([{ key: AugloopRealTimeTranscriptionConnectionKeys.OffsetAdjustmentMs, value: this.offsetAdjustmentMs}]);
          } else {
            this.speechSessionStartedTime = Date.now();
          }
        } catch (e: any) {
          this.onError(e);
        }
      },
      onSpeechSessionStopped: () => {
        this.speechEventEmitter.emit('SpeechSessionStopped');
      },
      onFinalText: (text: Map<string, string>, properties: Map<string, string>) => this.OnFinalResult(text, properties),
      onFragmentText: (text: Map<string, string>, properties: Map<string, string>) => this.OnPartialResult(text, properties),
      onSpeechRecognitionStarted: () => {
        try {
          LogEvents.AugloopRealTimeTranscriptionConnection.IncrementCount(AugloopRealTimeTranscriptionConnectionKeys.OnSpeechRecognitionStarted);

          // If we were just unpaused, add the length of the pause to our pause adjustment so we can remove it if the session gets reset.
          if (this.lastPausedTime != null) {
            const pauseAdjustment = Date.now() - this.lastPausedTime;
            this.pauseAdjustmentMs += pauseAdjustment;
            this.lastPausedTime = null;
          }
        } catch (e: any) {
          this.onError(e);
        }
      },
      onError: (error?: string | IError) => {
        if (error?.toString().indexOf(ERROR_AUGLOOP_ACTIVE_ANNOTATIONS) == -1 || this.attempt >= 3) {
          try {
            // Ignore this deprectated error. It is now handled by catching a promise rejection.
            if (error == MicrophoneError.ErrorAudioCaptureAsync
              || error == MicrophoneError.ErrorAudioDevicesNotFoundError
              || error == MicrophoneError.ErrorAudioNotReadableError
              || error == MicrophoneError.ErrorAudioPermissionDeniedError)
              return;

            LogEvents.AugloopRealTimeTranscriptionConnection.AddData(
              [{ key: AugloopRealTimeTranscriptionConnectionKeys.OnError, value: 'IAugloopSpeechObserverDecorator.onError: ' + ErrorStringFromException(error)}]);

            const augloopSpeechObserverError = new Error('IAugloopSpeechObserverDecorator.onError: ' + ErrorStringFromException(error));
            this.onError(augloopSpeechObserverError);
          } catch (e: any) {
            this.onError(e);
          }
        }
      },
      onSpeechEnd: () => {
        try {
          LogEvents.AugloopRealTimeTranscriptionConnection.IncrementCount(AugloopRealTimeTranscriptionConnectionKeys.OnSpeechEnd);
        } catch (e: any) {
          this.onError(e);
        }
      },
      onEventUpdate: (_eventInfo: any) => {},
      showDictationWarning: () => {},
      onWarning: (_error?: IError) => {},
      showPermissionPrompt: () => {}
    };

    constructor() {
      this.transcription = { transcriptSegments: [] };
      this.onError = (error: Error) => { throw error };
      this.onAudioBuffer = (_) => {};
      this.initPromise = this.PerformInitializeConnection();
      this.transcriptSegmentsMap = new Map();
      this.speechEventEmitter = new EventEmitter();

      // If augloop server closes the session and we reconnect, we need to reinitialize speech to text client (so annotations are re-activated).
      GetAugloopEventEmitter().on(AugloopEvent.SessionClose, () => {
        // Append this to initPromise so we block the operations that need initPromise.
        this.initPromise =
          Promise.all([
              this.initPromise,
              new Promise<void>((resolve) => { GetAugloopEventEmitter().once(AugloopEvent.SessionConnect, resolve) })
            ])
          .then(() => this.InitializeSpeechToTextClient());
      });
    }

    // Initializes a real-time connection.
    private async PerformInitializeConnection(): Promise<void> {
      LogEvents.AugloopRealTimeTranscriptionConnection.Start();
      await this.InitializeSpeechToTextClient();

      // VoiceSession is responsible for sending voice data to augloop.
      const alRuntimeManager = await EnsureAugloopRuntimeManager()
      this.voiceSession = new VoiceSession(alRuntimeManager as any /* TODO: ADO:4691502 remove cast */, this.fdSpeechClientObserver, true /*useFrontdoorWorkflow*/, undefined /*commandTelemetry*/, undefined /*sessionTelemetry*/, SpeechToTextProfile.ConversationTranscription);
    }

    public SetAudioBufferCallback(onAudioBuffer: (buffer: AudioBuffer) => void): void {
      this.onAudioBuffer = onAudioBuffer;
    }

    public SetErrorCallback(onError: (error: Error) => void): void {
      this.onError = onError;
    }

    public async InitializeConnection(locale: string): Promise<void> {
      this.attempt = 0;
      this.locale = locale;
      return this.initPromise;
    }

    // Starts capturing microphone input and sends the captured audio data through the real-time connection.
    public async StartMicrophone() {
      return this.ResumeConnection();
    }

    // Starts sending input through the real-time connection.
    public async ResumeConnection(): Promise<void> {
      if (this.isVoiceSessionActive)
        return Promise.resolve();

      await this.initPromise;
      this.isVoiceSessionActive = true;

      if (this.locale == null)
        throw new Error("Reached resume connection without a locale. Did we really initilize AugloopRealTimeTranscriptionConnection?");

      DictationSettingsManager.dictationSettings.dictationLanguage = this.locale;

      try {
        await this.voiceSession!.startVoiceSessionFromMicrophoneInput((buffer: AudioBuffer) => this.OnAudioBufferCallback(buffer) /*buffercallback*/, null, null);
      } catch (e) {
        if (e == MicrophoneError.ErrorAudioPermissionDeniedError)
          throw new RealTimeTranscriptionConnectionError(RealTimeTranscriptionConnectionErrors.MicrophonePermissionError);
        else if (e == MicrophoneError.ErrorAudioDevicesNotFoundError)
          throw new RealTimeTranscriptionConnectionError(RealTimeTranscriptionConnectionErrors.MicrophoneNotFoundError);
        else if (e == MicrophoneError.ErrorAudioNotReadableError)
          throw new RealTimeTranscriptionConnectionError(RealTimeTranscriptionConnectionErrors.MicrophoneNotReadableError);
        else
          throw e;
      }
    }

    // Stops sending input through the real-time connection
    public async PauseConnection(): Promise<void> {
        if (!this.isVoiceSessionActive)
          return Promise.resolve();

        await this.initPromise;
        this.isVoiceSessionActive = false;

        try {
          await this.voiceSession!.stopMicrophoneInputAsync();
        } catch (e) {
          LogError(LogEventNames.Warning, "Exception while stopping microphone connection" + e);
        }
        this.lastPausedTime = Date.now();

    }

    // Closes real-time connection
    public async CloseConnection(): Promise<void> {
      // If we are already closing, return that promise.
      if (this.closeConnectionPromise != null)
        return this.closeConnectionPromise;

      this.closeConnectionPromise = new Promise(async (resolve, reject) => {
        try {
          if (this.voiceSession) {
            // The voice workflow cancels the session when it receives an endVoiceSession
            // message while it's finishing the session (i.e. already processing another endVoiceSession message)
            // Send endVoiceSession twice to cancel the session. This is so we don't have to wait for any
            // pending speech to text results.
            this.voiceSession.endVoiceSession();
            this.voiceSession.endVoiceSession();
            await this.WaitForSessionEnd();
          }

          if (this.speechToTextClient) {
            await this.speechToTextClient.uninitializeAsync();
          }

          LogEvents.AugloopRealTimeTranscriptionConnection.End();
          this.closeConnectionPromise = null;
          resolve();
        } catch (e) {
          reject(e);
        }
      });

      return this.closeConnectionPromise;
    }

    private async WaitForSessionEnd(): Promise<void> {
      let didTimeout = false;
      // Have a 30 second timeout just in case we don't receive onSpeechSessionStopped.
      // This is admittedly a long timeout, but at the end of the session the pipeline
      // could have a significant amount of audio pending to be processed and we don't
      // want to cut off transcripts.
      const timeoutPromise = new Promise<void>((resolve, reject) => {
        setTimeout(() => {
          try {
            didTimeout = true;

            // Cancel the session so we are sure the session is done and it won't leak into the next live recording.
            if (this.voiceSession) {
              this.voiceSession.endVoiceSession();
            }

            resolve();
          } catch (e) {
            reject(e);
          }
        }, MAX_UNRESPONSIVENESS_WAIT_TIME_MILLISECONDS);
      });

      const speechSessionStoppedPromise = new Promise<void>((resolve, reject) => {
        try {
          this.speechEventEmitter.once('SpeechSessionStopped', resolve);
        } catch (e) {
          reject(e);
        }
      });

      await Promise.race([timeoutPromise, speechSessionStoppedPromise]);

      if (didTimeout) {
        LogError(LogEventNames.Warning, "Reached timeout when waiting for session to end");
      }
    }

    private UpdateMaxSpeakerId(speakerId: number | null | undefined) {
      if (speakerId != null) {
        if (this.maxSpeakerId == null) {
          this.maxSpeakerId = speakerId;
        } else {
          this.maxSpeakerId = Math.max(this.maxSpeakerId, speakerId);
        }
      }
    }

    // Returns the final transcript when it is available.
    public async EndSessionAndGetFinalTranscript(): Promise<ITranscription> {
      if (this.isFinalTranscriptAvailable)
        return this.transcription;

      // End the voice session so the end of audio signal is sent
      // and we receive the last final results.
      if (this.voiceSession) {
        this.voiceSession.endVoiceSession();
      }

      await this.WaitForSessionEnd();
      this.voiceSession = null;

      this.isFinalTranscriptAvailable = true;

      if (this.maxSpeakerId)
        LogEvents.AugloopRealTimeTranscriptionConnection.AddData([{ key: AugloopRealTimeTranscriptionConnectionKeys.MaxSpeakerId, value: this.maxSpeakerId }]);

      let iSessionEndOffsets = 0; // Index for iterating sessionEndOffsets, which contains session offsets.
      let maxNumSegments = 0; // Max number of segments in a single session.
      let currSessionNumSegments = 0; // Number of segments in the "current" session (pointed to by iSessionEndOffsets)
      let iSessionEndOffsetsMaxNumSegments = 0; // Index of the sessionEndOffsets entry with the max number of segments.

      // Dump transcriptSegmentsMap into the resulting transcript.
      // transcriptSegmentsMap is iterated in insertion order.
      this.transcriptSegmentsMap.forEach((segment: ITranscriptionSegment) => {
        // Do not include empty results in the final transcript.
        if (segment.text == '')
          return;

        this.transcription.transcriptSegments.push(segment);

        // If there was more than one session, compute the one with most segments.
        if (this.sessionEndOffsets.length > 1) {
          if (segment.end >=  secFromMs(this.sessionEndOffsets[iSessionEndOffsets])) {
            if (currSessionNumSegments > maxNumSegments) {
              maxNumSegments = currSessionNumSegments;
              iSessionEndOffsetsMaxNumSegments = iSessionEndOffsets;
            }

            iSessionEndOffsets++;
            currSessionNumSegments = 0;
          }
          currSessionNumSegments++;
        }
      });

      if (currSessionNumSegments > maxNumSegments) {
        maxNumSegments = currSessionNumSegments;
        iSessionEndOffsetsMaxNumSegments = iSessionEndOffsets;
      }

      // If there was more than one session, discard speaker IDs for all sessions except the one with the most segments.
      if (this.sessionEndOffsets.length > 1) {
        // Need to recompute maxSpeakerId just for the session with most segments.
        this.maxSpeakerId = null;

        this.transcription.transcriptSegments.forEach((segment: ITranscriptionSegment) => {
          if ((iSessionEndOffsetsMaxNumSegments == 0 || segment.start >= secFromMs(this.sessionEndOffsets[iSessionEndOffsetsMaxNumSegments - 1]))
            && segment.start <= secFromMs(this.sessionEndOffsets[iSessionEndOffsetsMaxNumSegments])) {
            this.UpdateMaxSpeakerId(segment.speakerId);
          } else {
            segment.speakerId = null;
          }
        });
      }

      this.transcription.speakerNames = new Array(this.maxSpeakerId != null ? this.maxSpeakerId + 1 : 0);

      // There's an issue in CTS where speech is returning responses out of order. Order our transcript by timestamp.
      this.transcription.transcriptSegments.sort((segment1: ITranscriptionSegment, segment2: ITranscriptionSegment) => {
        return segment1.start - segment2.start;
      });

      return this.transcription;
    }

    // Callback for final results
    private OnFinalResult(text: Map<string, string>, properties: Map<string, string>) {
      try {
        LogEvents.AugloopRealTimeTranscriptionConnection.IncrementCount(AugloopRealTimeTranscriptionConnectionKeys.OnFinalResult);
        LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: true, message: "Successfully received speech to text response", isFinalResponse: true });

        // Extract JsonResult from properties and parse it.
        const jsonResult = properties.get('JsonResult');
        const propertiesObj = JSON.parse(jsonResult!);

        const adjustedOffsetMs = msFrom100nsec(propertiesObj.Offset) + this.offsetAdjustmentMs;
        const startTimestamp = secFrom100nsec(propertiesObj.Offset) + Math.floor(this.offsetAdjustmentMs / 1000);
        const endTimestamp = startTimestamp + secFrom100nsec(propertiesObj.Duration);

        const speakerId = GetNumericSpeakerIdFromSpeakerId(propertiesObj.SpeakerId);
        const locale: string = DictationSettingsManager.dictationSettings.dictationLanguage!;
        const language: string = GetLanguageFromLocale(locale);
        this.UpdateMaxSpeakerId(speakerId);

        const segment: ITranscriptionSegment = {
          text: text.get(locale)!, language, start: startTimestamp, end: endTimestamp, speakerId
        };

        const key = GetCTSSpeakerIDWithOffsetKey(propertiesObj.SpeakerId, adjustedOffsetMs);

        if (this.transcriptSegmentsMap.has(key)) {
          LogEvents.AugloopRealTimeTranscriptionConnection.AddData([{ key: AugloopRealTimeTranscriptionConnectionKeys.ReceivedNearRTResult, value: true }]);
        }

        this.transcriptSegmentsMap.set(key, segment);
      } catch (e: any) {
        this.onError(e);
      }
    }

    // Callback for partial results
    private OnPartialResult(_text: Map<string, string>, properties: Map<string, string>) {
      try {
        LogEvents.AugloopRealTimeTranscriptionConnection.IncrementCount(AugloopRealTimeTranscriptionConnectionKeys.OnPartialResult);

        const jsonResult = properties.get('JsonResult');
        const propertiesObj = JSON.parse(jsonResult!);
        const startTimestamp = secFrom100nsec(propertiesObj.Offset) + Math.floor(this.offsetAdjustmentMs / 1000);
        const endTimestamp = startTimestamp + secFrom100nsec(propertiesObj.Duration);

        // Since we are using partial results, sometimes we receive results with an earlier offset.
        // Make sure we don't report anything less than the max we have reported so progress always moves forward.
        this.maxProgressUpdateTimestamp = Math.max(endTimestamp, this.maxProgressUpdateTimestamp);
      } catch (e: any) {
        this.onError(e);
      }
    }

    // Initialize the speech to text client.
    private async InitializeSpeechToTextClient() {
      if (this.closeConnectionPromise != null) {
        await this.closeConnectionPromise;
      }

      const alRuntimeManager = await EnsureAugloopRuntimeManager();

        this.speechToTextClient = new SpeechToTextClient(
          alRuntimeManager as any /* TODO: ADO:4691502 remove cast */,
          this.fdSpeechClientObserver /*fdObserver*/,
          true /*useFrontdoorWorkflow*/,
          true /*startRecognitionInSpeechToTextClient*/,
          true /* enableSetAnnotationsFromALVoiceClient */,
          SpeechToTextProfile.ConversationTranscription
        );

      let retryCount: number = 0;

      do {
        if (retryCount > 0) {
          await wait(RETRY_SETTINGS.retryWaitMilliseconds);
        }
        this.attempt++;
        await this.speechToTextClient.initializeAsync([SpeechToTextResultType.PartialResult, SpeechToTextResultType.FinalResult]);
      } while (!this.speechToTextClient.initialized && retryCount++ < RETRY_SETTINGS.maxRetries)
    }

    private OnAudioBufferCallback(buffer: AudioBuffer) {
      this.onAudioBuffer(buffer);
    }
}
